import { useCallback, useEffect, useRef, useState } from "react";
import { createSchedule, deleteSchedule, getSchedule, listSchedules, updateSchedule } from "../../services/configuration/configuration.service";
import type { CreateScheduleRequest, ScheduleDetail, ScheduleSummary } from "../../services/configuration/configuration.types";
import { TOAST_SUCCESS_MS } from "../../constants/timeouts";
import { buildSchedulePayload, getScheduleErrorMessage, getScheduleList } from "./scheduleForm";

export function useSchedules() {
    const [schedules, setSchedules] = useState<ScheduleSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalError, setModalError] = useState<string | null>(null);
    const [success, setSuccess] = useState<{ message: string } | null>(null);
    const requestId = useRef(0);

    const refresh = useCallback(async () => {
        const current = ++requestId.current;
        const response = await listSchedules();
        if (!response.success || !response.data) {
            throw new Error(response.error?.message || "Failed to load schedules");
        }
        if (current === requestId.current) setSchedules(getScheduleList(response.data));
    }, []);

    useEffect(() => {
        let mounted = true;
        void refresh()
            .catch((error: unknown) => {
                if (mounted) setError(getScheduleErrorMessage(error, "Failed to load schedules"));
            })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; requestId.current += 1; };
    }, [refresh]);

    useEffect(() => {
        if (!success) return;
        const timeout = setTimeout(() => setSuccess(null), TOAST_SUCCESS_MS);
        return () => clearTimeout(timeout);
    }, [success]);

    const loadDetails = async (id: number): Promise<ScheduleDetail | null> => {
        setLoadingDetails(true);
        setError(null);
        setModalError(null);
        try {
            const response = await getSchedule(id);
            if (!response.success || !response.data) {
                throw new Error(response.error?.message || "Failed to load schedule details");
            }
            return response.data;
        } catch (error: unknown) {
            setError(getScheduleErrorMessage(error, "Failed to load schedule details"));
            return null;
        } finally {
            setLoadingDetails(false);
        }
    };

    const save = async (values: CreateScheduleRequest, schedule: ScheduleDetail | null): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        setModalError(null);
        setSuccess(null);
        let persisted = false;
        try {
            const payload = buildSchedulePayload(values);
            const response = schedule ? await updateSchedule(schedule.id, payload) : await createSchedule(payload);
            if (!response.success || !response.data) {
                throw new Error(response.error?.errors?.join("; ") || response.error?.message || `Failed to ${schedule ? "update" : "create"} schedule`);
            }
            persisted = true;
            const saved = response.data;
            setSchedules(previous => schedule
                ? previous.map(item => item.id === schedule.id ? saved : item)
                : [...previous, saved]);
            await refresh();
            setSuccess({ message: schedule ? "Schedule updated" : "Schedule created" });
            return true;
        } catch (error: unknown) {
            const message = getScheduleErrorMessage(error, "Failed to save schedule");
            if (persisted) setError(`Schedule saved, but refreshing the list failed: ${message}`);
            else setModalError(message);
            return persisted;
        } finally {
            setSubmitting(false);
        }
    };

    const remove = async (id: number): Promise<boolean> => {
        setSubmitting(true);
        setError(null);
        setModalError(null);
        setSuccess(null);
        try {
            const response = await deleteSchedule(id);
            if (!response.success) throw new Error(response.error?.message || "Failed to delete schedule");
            setSchedules(previous => previous.filter(schedule => schedule.id !== id));
            setSuccess({ message: "Schedule deleted" });
            return true;
        } catch (error: unknown) {
            setModalError(getScheduleErrorMessage(error, "Failed to delete schedule"));
            return false;
        } finally {
            setSubmitting(false);
        }
    };

    return {
        schedules, loading, loadingDetails, submitting, error, modalError, success,
        setError, setModalError, setSuccess, loadDetails, save, remove,
    };
}
